import styled from '@emotion/styled';
import React from 'react';

import { customStylesWarning } from '../../console-messages';
import type {
  LensDivProps,
  ResponsiveGridSections,
  ResponsiveType,
} from '../../types';
import { getResponsiveOneOf, getSize } from '../../utilities';

import { getResponsiveGridSections } from '../../utilities/responsive';
import { getListContainer } from '../../utilities/styles';

const ArrangeWrapper = styled.div<ArrangeWrapperProps>`
  display: grid;
  ${props => getResponsiveOneOf('align-items', props.alignItems)};
  ${props => getResponsiveOneOf('justify-content', props.justifyContent)};
  ${props =>
    props.justifyItems &&
    getResponsiveOneOf('justify-items', props.justifyItems)};
  ${props =>
    props.alignContent &&
    getResponsiveOneOf('align-content', props.alignContent)};
  ${props =>
    !props.columns &&
    !props.rows &&
    !props.autoFlow &&
    'grid-auto-flow: column'};
  ${props => getResponsiveGridSections(props.columns, 'columns')};
  ${props => getResponsiveGridSections(props.rows, 'rows')};
  ${props => getSize('gap', props.gap)};
  ${props => getSize('width', props.width)};
  ${props => getSize('height', props.height)};
  ${props => getSize('min-width', props.minWidth)};
  ${props => getSize('min-height', props.minHeight)};
  ${props => getSize('max-width', props.maxWidth)};
  ${props => getSize('max-height', props.maxHeight)};

  ${props =>
    props.autoFlow && getResponsiveOneOf('grid-auto-flow', props.autoFlow)};
  ${props =>
    props.columns &&
    props.autoFlow &&
    getResponsiveOneOf('grid-auto-flow', props.autoFlow)};
  ${props => getListContainer(props.as)};
`;

const Arrange = ({
  children,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  gap,
  columns,
  rows,
  alignItems = 'center',
  justifyContent = 'start',
  justifyItems,
  alignContent,
  autoFlow,
  htmlTag = 'div',
  className,
  style,
  ...props
}: ArrangeProps &
  React.ComponentProps<typeof ArrangeWrapper> &
  Omit<
    LensDivProps,
    keyof ArrangeProps & React.ComponentProps<typeof ArrangeWrapper>
  >) => {
  if (className || style) {
    // eslint-disable-next-line no-console
    console.warn(customStylesWarning);
  }

  return (
    <ArrangeWrapper
      alignItems={alignItems}
      as={htmlTag}
      justifyContent={justifyContent}
      justifyItems={justifyItems}
      alignContent={alignContent}
      gap={gap}
      columns={columns}
      rows={rows}
      width={width}
      height={height}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      autoFlow={autoFlow}
      {...props}
    >
      {children}
    </ArrangeWrapper>
  );
};

interface SharedProps {
  children?: React.ReactNode;
  width?: ResponsiveType<string | number>;
  height?: ResponsiveType<string | number>;
  minWidth?: ResponsiveType<string | number>;
  minHeight?: ResponsiveType<string | number>;
  maxWidth?: ResponsiveType<string | number>;
  maxHeight?: ResponsiveType<string | number>;
  gap?: ResponsiveType<string | number> | null;
  alignItems?: ResponsiveType<
    'start' | 'end' | 'center' | 'stretch' | 'baseline'
  >;
  justifyItems?: ResponsiveType<'start' | 'end' | 'center' | 'stretch'>;
  alignContent?: ResponsiveType<
    | 'start'
    | 'end'
    | 'center'
    | 'stretch'
    | 'space-around'
    | 'space-between'
    | 'space-evenly'
  >;
  justifyContent?: ResponsiveType<
    | 'start'
    | 'end'
    | 'center'
    | 'stretch'
    | 'space-around'
    | 'space-between'
    | 'space-evenly'
  >;
  autoFlow?: ResponsiveType<'column' | 'row'>;
  columns?: ResponsiveGridSections;
  rows?: ResponsiveGridSections;
}

interface ArrangeProps extends SharedProps {
  htmlTag?: 'div' | 'ul' | 'ol' | 'li';
}

interface ArrangeWrapperProps extends SharedProps {
  as?: string;
}

export default Arrange;
